import React from 'react'
import PropTypes from 'prop-types'
import { Map } from 'immutable'
import { Calendar } from 'react-date-range'
import moment from 'moment'
import classNames from 'classnames'
import { PureDataComponent } from '@aytm/lib/common/components/pure-component'
import Label from '@aytm/lib/rfd-ui/components/basic/label'
import TextField from '@aytm/lib/rfd-ui/components/basic/text-field'

import '@aytm/lib/rfd-ui/styles/advanced/date-time-picker.scss'

const formatCharacters = {
  0: {
    validate: c => /\d/.test(c),
    transform: c => (parseInt(c, 10) > 1 ? `0` : c)
  }
}

export default class DateTimePicker extends PureDataComponent {
  static propTypes = {
    placeholder: PropTypes.string,
    timeZone: PropTypes.instanceOf(Map),
    minDate: PropTypes.number,
    maxDate: PropTypes.number,
    detached: PropTypes.bool.isRequired,
    empty: PropTypes.bool,
    disabled: PropTypes.bool,
    onChange: PropTypes.func
  }

  constructor(props) {
    super(props)

    this.showPicker = this.showPicker.bind(this)
    this.hidePicker = this.hidePicker.bind(this)
    this.onTimeBlur = this.onTimeBlur.bind(this)
    this.onTimeChange = this.onTimeChange.bind(this)
    this.onMeridiemClick = this.onMeridiemClick.bind(this)
    this.onCalendarChange = this.onCalendarChange.bind(this)
    this.change = this.change.bind(this)
    this.clearState = this.clearState.bind(this)
    this.renderPicker = this.renderPicker.bind(this)
    this.renderAttached = this.renderAttached.bind(this)
    this.renderDetached = this.renderDetached.bind(this)

    this.picker = null
    this.style = null
    this.link = null
    this.mounted = false
    this.listener = function (event) {
      if ((!this.link || !this.link.contains(event.target)) && (!this.picker || !this.picker.contains(event.target))) {
        this.hidePicker()
      }
    }.bind(this)

    const date = props.date ? moment(props.date) : moment()
    this.state = {
      shown: false,
      date: (date.isValid() ? date : moment()).utcOffset(props.timeZone.get('offset') || moment().utcOffset()),
      detachedValue: props.placeholder
    }
  }

  showPicker() {
    if (this.state.disabled) return

    this.setState({ shown: true })

    document.addEventListener('click', this.listener)
  }

  hidePicker() {
    document.removeEventListener('click', this.listener)

    this.setState({ shown: false })
  }

  onTimeBlur(event) {
    const value =
      this.refHoursInput && this.refHoursInput.refInput ? this.refHoursInput.refInput.value : event.target.value
    if (value) {
      this.onTimeChange(value.replace(/_/gi, '0'))
    }
  }

  onTimeChange(t) {
    if (!t.length || t.replace(/_/gi, '').length < 5) return

    const { date: currentDate } = this.state

    const meridiem = currentDate.format('A')

    const hours = Math.min(parseInt(t.split(':')[0], 10) || 0, 12) + ('AM' == meridiem ? 0 : 12)
    const minutes = Math.min(parseInt(t.split(':')[1], 10) || 0, 59)

    const newDate = moment(currentDate).set({
      hour: hours % 24,
      minute: minutes % 60
    })

    this.change(newDate)
  }

  onMeridiemClick() {
    const { date: currentDate } = this.state

    const hours = currentDate.get('hour') + (currentDate.get('hour') > 12 ? -1 : 1) * 12

    const newDate = moment(currentDate).set({ hour: hours % 24 })

    this.change(newDate)
  }

  onCalendarChange(date) {
    const { date: currentDate } = this.state

    const newDate = moment(currentDate).set({
      year: date.get('year'),
      month: date.get('month'),
      date: date.get('date')
    })

    this.change(newDate)
  }

  change(date) {
    const newDate = moment
      .max(moment(this.props.minDate), date)
      .utcOffset(this.props.timeZone.get('offset') || moment().utcOffset())

    if (newDate.isValid()) {
      const text = `${newDate.format('dddd, MMMM D, YYYY HH:mm A')} ${this.props.timeZone.get('name')}`
      this.props.onChange &&
        'function' == typeof this.props.onChange &&
        this.props.onChange(
          Map({
            date: moment(newDate),
            text: text
          })
        )
      this.setState({ date: newDate })
      this.props.detached && this.setState({ detachedValue: newDate.text })
    }
  }

  clearState() {
    const date = this.props.date ? moment(this.props.date) : moment()
    this.setState({
      shown: false,
      date: (date.isValid() ? date : moment()).utcOffset(this.props.timeZone.get('offset') || moment().utcOffset()),
      detachedValue: this.props.placeholder
    })
  }

  componentDidMount() {
    const el = this.link.querySelectorAll(':first-child')[0]
    this.style = {
      left:
        this.link.offsetLeft - this.link.scrollLeft + (this.props.left ? el.getBoundingClientRect().width - 742 : 0),
      top: this.link.offsetTop - this.link.scrollTop,
      marginTop: el.getBoundingClientRect().height
    }
    if (!this.props.date.isSame(this.state.date)) {
      this.props.onChange &&
        'function' == typeof this.props.onChange &&
        this.props.onChange(
          Map({
            date: moment(this.state.date),
            text: `${this.state.date.format('dddd, MMMM D, YYYY HH:mm A')} ${this.props.timeZone.get('name')}`
          })
        )
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.mounted && nextProps.empty && this.clearState()
    this.mounted && nextProps.disabled && this.state.shown && this.hidePicker()
  }

  renderPicker() {
    const { timeZone, minDate, maxDate } = this.props
    const { shown, date } = this.state
    const utcOffset = timeZone.get('offset') || moment().utcOffset()

    return (
      <div>
        {shown && (
          <div
            className="rfd-ui-date-time-picker-calendar"
            ref={el => {
              this.picker = el
            }}
            style={this.style}
          >
            <div className="rfd-ui-date-time-picker-calendar-time">
              <div className="rfd-ui-date-time-picker-calendar-time-label">time:</div>
              <div className="rfd-ui-date-time-picker-calendar-time-input">
                <TextField
                  className="rfd-ui-text-field-simple"
                  ref={this.saveRef('HoursInput')}
                  width={68}
                  value={date.format('hh:mm')}
                  maskPattern="01:11"
                  maskPlaceholderChar="_"
                  maskFormatCharacters={formatCharacters}
                  maskSub="__:__"
                  onBlur={this.onTimeBlur}
                  onChange={this.onTimeChange}
                />
              </div>
              <div className="rfd-ui-date-time-picker-calendar-time-meridiem" onClick={this.onMeridiemClick}>
                {date.format('A')}
              </div>
            </div>
            <Calendar
              date={moment(date)}
              minDate={moment(minDate).utcOffset(utcOffset)}
              maxDate={moment(maxDate).utcOffset(utcOffset)}
              onChange={this.onCalendarChange}
            />
          </div>
        )}
      </div>
    )
  }

  renderAttached() {
    return (
      <div>
        <div
          className="rfd-ui-date-time-picker-link"
          ref={div => {
            this.link = div
          }}
          onClick={this.showPicker}
        >
          {this.props.children}
        </div>
        {this.renderPicker()}
      </div>
    )
  }

  renderDetached() {
    return (
      <div>
        <div
          className="rfd-ui-date-time-picker-link"
          ref={div => {
            this.link = div
          }}
          onClick={this.showPicker}
        >
          <Label
            icon="calendar"
            withArrow={true}
            placeholder={this.state.detachedValue}
            width={this.props.detachedWidth ? this.props.detachedWidth : 231}
          />
        </div>
        {this.renderPicker()}
      </div>
    )
  }

  render() {
    return (
      <div
        className={classNames('rfd-ui-date-time-picker', {
          'rfd-ui-date-time-picker-disabled': this.props.disabled
        })}
      >
        {this.props.detached ? this.renderDetached() : this.renderAttached()}
      </div>
    )
  }
}
